import React, { useMemo } from 'react';
import ClinicalGeneratedCard from './ClinicalGeneratedCard.jsx';

const SaveIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
    </svg>
);

const ClinicalGeneratedCards = ({
    cards,
    selectedMap,
    onSelectedMapChange,
    folders,
    targetFolderId,
    onTargetFolderChange,
    onSaveToFolder,
    isSaving,
}) => {
    const count = cards.length;
    const allSelected = count > 0 && cards.every((c) => selectedMap[c.id]);
    const selectedCount = Object.values(selectedMap).filter(Boolean).length;
    const saveCount = selectedCount > 0 ? selectedCount : count;

    const toggleSelectAll = () => {
        if (allSelected) {
            onSelectedMapChange({});
            return;
        }
        const next = {};
        cards.forEach((c) => {
            next[c.id] = true;
        });
        onSelectedMapChange(next);
    };

    const folderOptions = useMemo(
        () =>
            folders.map((f) => (
                <option key={f.id} value={f.id}>
                    {f.name}
                </option>
            )),
        [folders]
    );

    if (count === 0) return null;

    return (
        <section
            className="mt-6 flex max-h-[min(70vh,42rem)] flex-col gap-4 rounded-3xl border border-white/10 bg-slate-900/40 p-4 sm:p-5"
            aria-label="Generated cards"
        >
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg font-semibold tracking-tight text-white">
                    Generated Cards ({count})
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleSelectAll}
                        className="inline-flex items-center rounded-xl border border-white/20 bg-transparent px-4 py-2 text-sm font-medium text-white hover:bg-white/5 transition-colors"
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                    <button
                        type="button"
                        onClick={onSaveToFolder}
                        disabled={isSaving || !targetFolderId}
                        title={!targetFolderId ? (folders.length === 0 ? 'Create a folder in your library first' : 'Choose a folder below') : undefined}
                        className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md shadow-teal-900/30"
                    >
                        <SaveIcon />
                        {isSaving ? 'Saving…' : saveCount === count ? 'Save All to Folder' : `Save ${saveCount} to Folder`}
                    </button>
                </div>
            </div>

            <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                <label htmlFor="clinical-save-folder" className="text-xs text-slate-400 shrink-0">
                    Save to folder
                </label>
                {folders.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No folders yet — create one in your library to save cards here.</p>
                ) : (
                    <select
                        id="clinical-save-folder"
                        className="w-full sm:max-w-xs rounded-xl bg-slate-950/60 border border-white/10 px-3.5 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                        value={targetFolderId}
                        onChange={(e) => onTargetFolderChange(e.target.value)}
                        disabled={isSaving}
                    >
                        <option value="">Choose a folder…</option>
                        {folderOptions}
                    </select>
                )}
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
                {cards.map((card, idx) => (
                    <ClinicalGeneratedCard
                        key={card.id}
                        card={card}
                        selected={!!selectedMap[card.id]}
                        onSelectedChange={(checked) =>
                            onSelectedMapChange((prev) => ({
                                ...prev,
                                [card.id]: checked,
                            }))
                        }
                        defaultExpanded={idx === 0}
                    />
                ))}
            </div>
        </section>
    );
};

export default ClinicalGeneratedCards;
